import { createSlice } from '@reduxjs/toolkit';

export const MAX_GROUP_MEMBERS = 5;

const mergeSessions = (existingSessions, newSessions) => {
  const existingMap = new Map(existingSessions.map(session => [session['messagesession-id'], session]));
  newSessions.forEach(newSession => {
    existingMap.set(newSession['messagesession-id'], newSession);
  });
  return Array.from(existingMap.values()) //sorting the arrays
    .sort((a, b) => {
      const dateA = new Date(a['messagesession-last-datetime']);
      const dateB = new Date(b['messagesession-last-datetime']);
      return dateB - dateA;
    });
};

export const messageSessionSlice = createSlice({
  name: 'messageSession',
  initialState: {
    messageSession: [],
    pinnedMessageSessions: [],
    selectedMessageSessionId: null,
    summaries: {},
    loading: true,
    error: null,
  },
  reducers: {
    setMessageSession: (state, action) => {
      const fetchedSessions = action.payload;
      const fetchedSessionIds = new Set(fetchedSessions.map(session => session['messagesession-id']));

      const remoteIdsMap = new Set(
        fetchedSessions
          .filter(s => s['messagesession-remote'])
          .map(s => String(s['messagesession-remote']).split('@')[0])
      );

      // Filter out sessions that no longer exist in the fetched list
      state.messageSession = state.messageSession.filter(session => {
        if (session['messagesession-last-status'] === 'temporary') {
          return !remoteIdsMap.has(session['messagesession-remote']);
        }
        return fetchedSessionIds.has(session['messagesession-id']);
      });
      state.messageSession = mergeSessions(state.messageSession, fetchedSessions);
      state.loading = false;
    },
    setSessionSummary: (state, action) => {
      const { sessionId, summary } = action.payload;
      state.summaries[sessionId] = summary;
    },
    setSelectedMessageSessionId: (state, action) => {
      state.selectedMessageSessionId = action.payload;
    },
    addTemporarySession: (state, action) => {
      const temporaryMessageSession = action.payload;
      state.messageSession.unshift(temporaryMessageSession);
    },
    updateMessageSessionStatus: (state, action) => {
      const { messageSessionId, status } = action.payload;
      state.messageSession = state.messageSession.map(session =>
        session['messagesession-id'] === messageSessionId
          ? { ...session, 'messagesession-last-status': status }
          : session
      );
    },
    updateMessageSessionNotification: (state, action) => {
      const { messageSessionId, muted } = action.payload;
      state.messageSession = state.messageSession.map(session =>
        session['messagesession-id'] === messageSessionId
          ? { ...session, 'messagesession-muted': muted ? 'yes' : 'no' }
          : session
      );
    },
    deleteMessageSession: (state, action) => {
      const messageSessionId = action.payload;
      state.messageSession = state.messageSession.filter(
        session => session['messagesession-id'] !== messageSessionId
      );
      // Also remove from pinned sessions if present
      if (state.pinnedMessageSessions.includes(messageSessionId)) {
        state.pinnedMessageSessions = state.pinnedMessageSessions.filter(sessionId => sessionId !== messageSessionId);
      }
      if (state.selectedMessageSessionId === messageSessionId) {
        state.selectedMessageSessionId = null;
      }
      delete state.summaries[messageSessionId];
    },
    addPinnedMessageSession: (state, action) => {
      if (!state.pinnedMessageSessions.includes(action.payload)) {
        state.pinnedMessageSessions.push(action.payload);
      }
    },
    removePinnedMessageSession: (state, action) => {
      state.pinnedMessageSessions = state.pinnedMessageSessions.filter(sessionId => sessionId !== action.payload);
    },
    setPinnedMessageSessions: (state, action) => {
      state.pinnedMessageSessions = action.payload;
    },
    resetMessageSession: (state) => {
      state.messageSession = [];
      state.pinnedMessageSessions = [];
      state.selectedMessageSessionId = null;
      state.summaries = {};
      state.loading = true;
      state.error = null;
    },

  },
});

export const {
  setMessageSession,
  setSessionSummary,
  setSelectedMessageSessionId,
  addTemporarySession,
  updateMessageSessionStatus,
  updateMessageSessionNotification,
  deleteMessageSession,
  addPinnedMessageSession,
  removePinnedMessageSession,
  setPinnedMessageSessions,
  resetMessageSession,
} = messageSessionSlice.actions;

//Selectors
export const selectMessageSessions = (state) => state.messageSession;
export const selectSessionSummaries = state => state.messageSession.summaries;
export const selectPinnedMessageSessions = (state) => state.messageSession.pinnedMessageSessions;

export default messageSessionSlice.reducer;